import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Title, Text, Card, Group, ThemeIcon, Box, Stack,
  Button, Badge, Divider, UnstyledButton, Drawer, TextInput,
  Textarea, Checkbox, NumberInput, ActionIcon, Skeleton, Tooltip,
  ColorPicker,
} from '@mantine/core';
import { IconBook2, IconUser, IconArrowLeft, IconPlus, IconPencil, IconTrash, IconRefresh, IconMapPin } from '@tabler/icons-react';
import { Table, ConfirmDialog } from '@/components';
import { toast } from '@/components/ui/providers/AlertProvider';
import { useSessionStatusService, SessionStatus } from '@actschurch/ccms-sdk';

const ALL_CATEGORIES = [
  { label: 'Acts Care Courses',    icon: IconBook2, link: '/LookUp/ActsCareCourses',    active: true  },
  { label: 'Acts Care Counseling', icon: IconUser,  link: '/LookUp/ActsCareCounseling', active: false },
   { label: 'Locations', icon: IconMapPin,  link: '/LookUp/Location', active: false  },
];

const EMPTY: SessionStatus = {
  statusID: 0, name: '', description: '', color: '', icon: '',
  isActive: false, createdDateTime: 0, modifiedDateTime: 0,
};

function ShimmerRow() {
  return (
    <Table.Tr>
      {Array.from({ length: 7 }).map((_, i) => (
        <Table.Td key={i}><Skeleton height={14} radius="sm" /></Table.Td>
      ))}
    </Table.Tr>
  );
}

export default function SessionStatusPage() {
  const navigate = useNavigate();
  const service = useSessionStatusService();

  const [statuses, setStatuses]       = useState<SessionStatus[]>([]);
  const [loading, setLoading]         = useState(false);
  const [addOpen, setAddOpen]         = useState(false);
  const [editOpen, setEditOpen]       = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [form, setForm]               = useState<SessionStatus>(EMPTY);
  const [selectedID, setSelectedID]   = useState(0);

  // toast is handled by provider

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try { setStatuses(await service.getAll()); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [service]);

  useEffect(() => { fetchAll(); }, []);

  const resetForm = () => setForm(EMPTY);

  const handleAdd = async () => {
    setLoading(true);
    try {
      await service.create(form);
      await fetchAll();
      resetForm();
      setAddOpen(false);
      toast.success('Created', 'Session status created successfully.');
    } catch (e) {
      console.error(e);
      setLoading(false);
      toast.error('Error', 'Failed to create session status.');
    }
  };

  const handleUpdate = async () => {
    setLoading(true);
    try {
      await service.update(selectedID, form);
      await fetchAll();
      resetForm();
      setEditOpen(false);
      toast.success('Updated', 'Session status updated successfully.');
    } catch (e) {
      console.error(e);
      setLoading(false);
      toast.error('Error', 'Failed to update session status.');
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await service.delete(selectedID);
      await fetchAll();
      setConfirmOpen(false);
      toast.warning('Deleted', 'Session status deleted successfully.');
    } catch (e) {
      console.error(e);
      setLoading(false);
      toast.error('Error', 'Failed to delete session status.');
    }
  };

  const openEdit = (s: SessionStatus) => {
    setSelectedID(s.statusID); setForm({ ...s }); setEditOpen(true);
  };

  const StatusForm = (
    <Stack>
      <TextInput label="Name" value={form.name ?? ''}
        onChange={(e) => setForm({ ...form, name: e.currentTarget.value })} />
      <Text>Choose Colour</Text>
      <ColorPicker
        value={form.color || '#4CAF50'}
        onChange={(value) => setForm({ ...form, color: value })}
        format="hex"
        swatches={[
          '#e03131', '#c2255c', '#9c36b5', '#3b5bdb', '#1971c2',
          '#0c8599', '#099268', '#2f9e44', '#e8590c', '#f08c00',
        ]}
        swatchesPerRow={10}
        fullWidth
      />
      <TextInput label="Icon" value={form.icon ?? ''}
        onChange={(e) => setForm({ ...form, icon: e.currentTarget.value })} />
      <Textarea label="Description" value={form.description ?? ''}
        onChange={(e) => setForm({ ...form, description: e.currentTarget.value })} />
      <Checkbox label="Is Active" checked={form.isActive}
        onChange={(e) => setForm({ ...form, isActive: e.currentTarget.checked })} />
    </Stack>
  );

  return (
    <Container size="xl" py="xl">
      <Drawer opened={addOpen} onClose={() => { setAddOpen(false); resetForm(); }}
        title="Add Session Status" position="right" size="md" padding="md">
        <NumberInput label="Status ID" required
          value={form.statusID} onChange={(v) => setForm({ ...form, statusID: Number(v) })} />
        {StatusForm}
        <Group justify="flex-end" mt="lg">
          <Button variant="subtle" onClick={() => { setAddOpen(false); resetForm(); }}>Cancel</Button>
          <Button onClick={handleAdd}>Save</Button>
        </Group>
      </Drawer>

      <Drawer opened={editOpen} onClose={() => { setEditOpen(false); resetForm(); }}
        title="Update Session Status" position="right" size="md" padding="md">
        {StatusForm}
        <Group justify="flex-end" mt="lg">
          <Button variant="subtle" onClick={() => { setEditOpen(false); resetForm(); }}>Cancel</Button>
          <Button onClick={handleUpdate}>Update</Button>
        </Group>
      </Drawer>

      <ConfirmDialog opened={confirmOpen} onClose={() => setConfirmOpen(false)}
        onConfirm={handleDelete} type="danger" title="Delete this status?"
        message="This action cannot be undone." confirmLabel="Delete" cancelLabel="Cancel" />

      <Group align="flex-start" gap="xl" wrap="nowrap">
        <Box w={220} style={{ flexShrink: 0 }}>
          <Button variant="subtle" leftSection={<IconArrowLeft size={14} />}
            mb="lg" px={0} onClick={() => navigate('/LookUp/ActsCareCourses')}>
            All Lookups
          </Button>
          <Text size="xs" fw={700} tt="uppercase" c="dimmed" mb="sm">Categories</Text>
          <Stack gap={4}>
            {ALL_CATEGORIES.map((cat) => (
              <UnstyledButton key={cat.label} onClick={() => navigate(cat.link)}
                style={(theme) => ({
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '8px 10px', borderRadius: theme.radius.sm,
                  backgroundColor: cat.active ? theme.colors.blue[9] + '33' : 'transparent',
                  color: cat.active ? theme.colors.blue[4] : theme.colors.gray[4],
                  fontWeight: cat.active ? 600 : 400,
                })}>
                <ThemeIcon variant={cat.active ? 'light' : 'subtle'}
                  color={cat.active ? 'blue' : 'gray'} size="sm" radius="sm">
                  <cat.icon size={14} />
                </ThemeIcon>
                <Text size="sm">{cat.label}</Text>
              </UnstyledButton>
            ))}
          </Stack>
        </Box>

        <Divider orientation="vertical" />

        <Box style={{ flex: 1 }}>
          <Group justify="space-between" mb="lg">
            <Group gap="md">
              <ThemeIcon variant="light" color="blue" size="lg" radius="md">
                <IconBook2 size={18} />
              </ThemeIcon>
              <div>
                <Title order={3}>Session Status</Title>
                <Text size="sm" c="dimmed">Scheduled, In Progress, Done states</Text>
              </div>
            </Group>
            <Group>
              <Tooltip label="Refresh">
                <ActionIcon variant="subtle" onClick={fetchAll} loading={loading}>
                  <IconRefresh size={16} />
                </ActionIcon>
              </Tooltip>
              <Button leftSection={<IconPlus size={16} />} onClick={() => setAddOpen(true)}>Add Item</Button>
            </Group>
          </Group>

          <Card withBorder radius="md" padding="lg">
            <Table tableVariant="default">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>ID</Table.Th>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>Icon</Table.Th>
                  <Table.Th>Color</Table.Th>
                  <Table.Th>Description</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th></Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {loading
                  ? Array.from({ length: 5 }).map((_, i) => <ShimmerRow key={i} />)
                  : statuses.map((s) => (
                    <Table.Tr key={s.statusID}>
                      <Table.Td>{s.statusID}</Table.Td>
                      <Table.Td fw={600}>{s.name}</Table.Td>
                      <Table.Td>{s.icon}</Table.Td>
                      <Table.Td>
                        {s.color ? (
                          <Group gap={6}>
                            <Box w={16} h={16} style={{ borderRadius: 4, backgroundColor: s.color, border: '1px solid rgba(255,255,255,0.15)', flexShrink: 0 }} />
                            <Text size="sm">{s.color}</Text>
                          </Group>
                        ) : '—'}
                      </Table.Td>
                      <Table.Td>{s.description}</Table.Td>
                      <Table.Td>
                        <Badge color={s.isActive ? 'green' : 'red'} variant="light" size="sm">
                          {s.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Group gap={4}>
                          <ActionIcon variant="subtle" onClick={() => openEdit(s)}>
                            <IconPencil size={16} color="orange" />
                          </ActionIcon>
                          <ActionIcon variant="subtle" onClick={() => { setSelectedID(s.statusID); setConfirmOpen(true); }}>
                            <IconTrash size={16} color="red" />
                          </ActionIcon>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))}
              </Table.Tbody>
            </Table>
          </Card>
        </Box>
      </Group>
    </Container>
  );
}